import useSWRImmutable from 'swr/immutable'
import { Fragment, useEffect, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import Header from '~/components/Header'
import { AppLanguage } from '~/types/locale'
import { Breakpoint, useBreakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { SANITY } from '~/services/sanity.service'
import { applyClassNames } from '~/utils'
import { isFile, populateShouldRenderChildren } from '~/../utils/data-fetch/theory/theory.query'
import { TopicOverview, TheoryType, Topic, PreviewTopic } from '~/types/theory'
import Preview from '~/encyclopedia/pages/Preview.page'
import TheoryArticleView from '~/encyclopedia/views/TheoryArticleView'
import GeoGebraNoDescription from '~/encyclopedia/components/GeoGebraNoDescription'
import ArrowLeftBoldIcon from '~/components/Icons/svgs/ArrowLeftBoldIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'

import IntroMessage from './IntroMessage'
import { ChapterButton } from './ChapterButton'
import { HorizontalMenuItem } from './HorizontalMenuItem'
import { SubChapter } from './SubChapter'
import BreadCrumbs from './BreadCrumbs'

function findNextUrl(selectedTopic: TopicOverview): string {
  return selectedTopic.path
}

function useTex(type: TheoryType, language: AppLanguage, initialChapters: TopicOverview[]): TopicOverview[] {
  const breakpoint = useBreakpoint()
  // Will only fetch the entire topic overview on desktop screens
  const doFetch = breakpoint >= Breakpoint.XL
  const { data } = useSWRImmutable<{ topicOverview: TopicOverview[] } | null>(`/${type}/${language}/{${doFetch}}`, () =>
    doFetch ? SANITY.getEncyclopediaTopOverview({ language, type }) : Promise.resolve(null),
  )
  return useMemo(
    () => (data?.topicOverview ? populateShouldRenderChildren(data.topicOverview) : initialChapters),
    [data, initialChapters],
  )
}

export function Theory({
  articleBody,
  topic,
  topicOverview,
  previewTopics,
}: {
  articleBody: string
  topic: Topic
  topicOverview: TopicOverview[]
  previewTopics: PreviewTopic[]
}): React.ReactElement {
  const { t } = useTranslation('encyclopedia')
  const { type, language, h1 } = topic
  const router = useRouter()
  const { locale } = useRouter()
  const isNorwegian = locale === AppLanguage.NO

  const topicIsFile = isFile(topic)
  const chapters = useTex(type, language, topicOverview)
  const isRootPath = 'isRootPath' in topic && topic.isRootPath
  const isEncyclopediaRoot = type === TheoryType.ENCYCLOPEDIA && isRootPath
  const isGeogebraRoot = type === TheoryType.GEOGEBRA && isRootPath
  const breakpoint = useBreakpoint()

  const shouldRenderChildren = topic.shouldRenderChildren
  const hideMenuTablet = topicIsFile
  const hideMenuMobile = !shouldRenderChildren
  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.LG })

  // TODO: This should be controlled from the admin panel, ClickUp task: https://app.clickup.com/t/8697pf7em

  useEffect(() => {
    if (!shouldRenderChildren || topicIsFile) {
      window.scrollTo(0, 0)
    }
  }, [topicIsFile, shouldRenderChildren])

  const containerClassNames = {
    box: topicIsFile ? 'flex flex-col items-stretch' : '',
    spacing: 'xl:space-y-4',
  }

  const horizontalNavClassNames = {
    box: {
      model: 'flex items-center justify-start',
      style: 'rounded shadlow-large bg-white',
    },
    position: 'relative self-start',
    size: 'w-full',
    spacing: 'px-3 space-x-1 2xl:space-x-2',
  }

  return (
    <div className="relative min-h-screen lg:flex lg:flex-row py-1 px-4 lg:px-8 sm:px-16 mt-5">
      <div className="cursor-pointer" onClick={() => router.back()}>
        <SVGIcon icon={ArrowLeftBoldIcon} pathClassName="text-white" size={24} />
      </div>
      <section className="max-w-5xl mx-auto lg:min-w-[768px]">
        <div className="space-y-4">
          {isDesktop && isRootPath && (
            <Header className={`invisible xl:visible h-0 xl:h-auto my-0 `} isTextWhite>
              {h1}
            </Header>
          )}

          <div className={applyClassNames(containerClassNames)}>
            {breakpoint >= Breakpoint.XL && (
              <div className={applyClassNames(horizontalNavClassNames)}>
                {chapters?.map((chapter, index) => {
                  if (index !== chapters.length - 1) {
                    return (
                      <Fragment key={chapter.path}>
                        <HorizontalMenuItem topic={chapter} />
                        <div className="shrink-0 w-px h-4 bg-gray-light-fade" />
                      </Fragment>
                    )
                  }
                  return <HorizontalMenuItem key={chapter.path} topic={chapter} />
                })}
              </div>
            )}
            <div className="w-full flex flex-col items-center">
              <BreadCrumbs breadcrumbs={topic.breadcrumbs} isWhite />
              {topicIsFile && <TheoryArticleView articleBody={articleBody} installsLearningContext topic={topic} />}
            </div>
          </div>

          {isEncyclopediaRoot && breakpoint < Breakpoint.MD && <IntroMessage />}
          <div className="relative md:grid md:grid-cols-2 md:gap-x-8 justify-between items-start">
            {breakpoint <= Breakpoint.LG && (!hideMenuTablet || (breakpoint <= Breakpoint.MD && !hideMenuMobile)) && (
              <div
                className={
                  'w-full p-6 md:p-4 mr-0 lg:mr-6  ' +
                  'flex flex-col justify-start align-center ' +
                  'rounded bg-white shadow-large'
                }
              >
                <h1 className="my-4 text-purple font-semibold text-h3 text-center">{h1}</h1>
                {chapters?.map((chapter) => {
                  const isSelected = topic.path.startsWith(chapter.path)
                  const chapterHeight = isSelected ? 'max-h-fit-content xl:max-h-encyclopedia' : 'max-h-0'

                  return (
                    <Fragment key={chapter.path}>
                      <ChapterButton findNextUrl={findNextUrl} isSelected={isSelected} topic={chapter} />
                      {chapter.shouldRenderChildren && (
                        <div
                          className={`flex flex-col ml-20 overflow-hidden ${chapterHeight} transition-all duration-500 ease-in-out`}
                        >
                          {chapter.children?.map((subChapter) => (
                            <SubChapter
                              findNextUrl={findNextUrl}
                              isSelectedTopic={(subTopic) => topic.path.startsWith(subTopic.path)}
                              key={subChapter.path}
                              topic={subChapter}
                            />
                          ))}
                        </div>
                      )}
                    </Fragment>
                  )
                })}
              </div>
            )}

            {previewTopics.length > 0 && (hideMenuMobile || breakpoint >= Breakpoint.MD) && (
              <div className="xl:col-span-2">
                <Preview previewTopics={previewTopics} />
              </div>
            )}
            {isEncyclopediaRoot && breakpoint >= Breakpoint.MD && (
              <div className="xl:col-span-2 xl:mt-2 flex md:flex-col xl:flex-row">
                <div className="self-start w-full xl:w-80 xl:order-2">
                  <IntroMessage />
                </div>
              </div>
            )}
          </div>
          {hideMenuMobile && breakpoint < Breakpoint.MD && (
            <div className="flex flex-col w-full">
              <div className="w-1/2 mb-4 mx-auto flex justify-center">
                <ButtonPrimary href={`/${type}`} isFullWidth link="internal" size="small">
                  {t('back')}
                </ButtonPrimary>
              </div>
            </div>
          )}
        </div>
        {isGeogebraRoot && isNorwegian && <GeoGebraNoDescription />}
      </section>
    </div>
  )
}
